import React, { FC, useCallback } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Col, Row } from 'reactstrap';
import * as Shared from '../../../../shared/options';
import {
  ActionButtonModal,
  LinkButton,
  MaterialIcon,
  PageHeader,
  UserStateComponent,
} from '../../../components';
import { validOptionTypes } from '../../../models';
import { useTranslation } from '../../../translations';
import { Fetcher, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { CreateOption } from './createOption';
import { OptionElement } from './optionElement';
import { ProductOptionBadges } from './utils';

interface OptionsContainerProps {
  readonly source: Shared.OptionSource;
}

export const OptionsContainer: FC<OptionsContainerProps> = ({ source }) => {
  const { ts } = useTranslation();
  const match = useRouteMatch();

  return (
    <UserStateComponent>
      <PageHeader>{ts('options')}</PageHeader>
      <Switch>
        <Route exact path={match.url} render={() => <ListOptions source={source} />} />
        <Route
          exact
          path={`${match.url}/new`}
          render={() => <CreateOptionFrame source={source} url={match.url} />}
        />
        <Route
          exact
          path={`${match.url}/:id`}
          render={(e) => (
            <OptionElement optionId={Number.parseInt(e.match.params.id, 10)} source={source} />
          )}
        />
      </Switch>
    </UserStateComponent>
  );
};

const ListOptions: FC<{ readonly source: Shared.OptionSource }> = ({ source }) => {
  const config = Shared.SourceConfig[source];
  const match = useRouteMatch();
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => await api.getOptions(source), [source]);

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      {config.allowCreate && (
        <>
          <div>
            <LinkButton color="primary" id="createOption" to={`${match.url}/new`}>
              {ts('create_new_option')}
            </LinkButton>
          </div>
          <hr />
        </>
      )}
      <div>
        <Row className="justify-content-center">
          {fetcher.data.map((option) => (
            <Col className="mb-10" key={option.id} lg={8} xs={12}>
              <OptionListElement onChange={fetcher.refresh} option={option} url={match.url} />
            </Col>
          ))}
        </Row>
      </div>
    </>
  );
};

const CreateOptionFrame: FC<{ readonly source: Shared.OptionSource; readonly url: string }> = ({
  source,
  url,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();
  const onCreate = useCallback(
    (option: Shared.Option) => {
      history.push(`${url}/${option.id}`);
    },
    [history, url],
  );

  return (
    <>
      <div>
        <LinkButton color="secondary" id="cancelOption" to={url}>
          {ts('back_to')} {ts('options')}
        </LinkButton>
      </div>
      <hr />
      <div>
        <Row className="justify-content-center">
          <Col lg={8} xs={12}>
            <CreateOption onCreate={onCreate} source={source} />
          </Col>
        </Row>
      </div>
    </>
  );
};

interface OptionListElementProps {
  readonly option: Shared.Option;
  readonly url: string;
  readonly onChange: () => void;
}

const OptionListElement: FC<OptionListElementProps> = ({ url, option, onChange }) => {
  const { ts } = useTranslation();
  const key = `option${option.id}`;
  const optionType = validOptionTypes[option.type];
  const deleteOption = useCallback(async () => {
    try {
      await api.deleteOption(option.source, option.id);
      toast.success(ts('option_has_been_deleted'));
      onChange();
    } catch (error) {
      captureError(error as Error);
    }
  }, [onChange, option.id, option.source, ts]);

  if (optionType.hidden) {
    return null;
  }

  return (
    <Card id={key}>
      <CardBody>
        <div
          style={{
            minWidth: '200px',
            position: 'absolute',
            right: '15px',
            textAlign: 'right',
            top: '15px',
          }}
        />
        <h4 style={{ marginRight: '30px' }}>
          {option.name} <small>({ts(optionType.name)})</small>
        </h4>
        <div className="float-end">
          <LinkButton color="primary" id={`${key}Edit`} to={`${url}/${option.id}`}>
            <MaterialIcon name="edit" />
          </LinkButton>{' '}
          <ActionButtonModal
            actionContent="common.delete"
            buttonContent={<MaterialIcon name="delete" />}
            color="danger"
            id={`${key}Delete`}
            onComplete={deleteOption}
            title="delete_option"
          >
            {ts('are_you_sure_you_want_to_delete')}
          </ActionButtonModal>
        </div>
        <ProductOptionBadges option={option} />
      </CardBody>
    </Card>
  );
};
